// src/components/finanzas/Finanzas.js
// Pantalla principal. Administrador: Dashboard (¿qué necesito atender?).
// Cajero: solo su checklist de pagos del día, sin información financiera.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { getDashboard, getCuentasEntreSucursales } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { toMoney } from '../../utils/numbers';
import { nombreTurno } from '../../utils/turnos';
import { EstadoBadge, fechaCorta, relativo } from './estados';
import DepositosModal from './DepositosModal';
import CierreDetalleModal from '../cierres/CierreDetalleModal';
import './Finanzas.css';
import './ui.css';
import './Dashboard.css';

/* ========= Helpers ========= */
// Texto del botón según la tarea (el flujo de cada cierre lo decide el servidor).
const ACCION_TAREA = { registrar: 'Registrar', revisar: 'Revisar', instrucciones: 'Generar', completar: 'Completar', liquidar: 'Liquidar' };

export default function Finanzas({ redirectToAgenda = false }) {
  const { sucursalId, isAdmin, loaded } = useSucursalActiva();
  if (!loaded) return <div className="home-shell"><p className="fx-empty">Cargando…</p></div>;
  if (!isAdmin) return <Navigate to="/Finanzas/Caja" replace />;
  return redirectToAgenda ? <Navigate to="/Finanzas/Agenda" replace /> : <Dashboard sucursalId={sucursalId} />;
}

/* ============================ Dashboard (Admin) ============================ */
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

function Kpi({ titulo, valor, detalle, tono = 'neutro', onClick, accion }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} className={`dash-kpi dash-kpi--${tono}`} onClick={onClick}>
      <span className="dash-kpi-titulo">{titulo}</span>
      <strong className="dash-kpi-valor">{valor}</strong>
      {detalle && <span className="dash-kpi-detalle">{detalle}</span>}
      {accion && <span className="dash-kpi-accion">{accion} →</span>}
    </Tag>
  );
}

function Dashboard({ sucursalId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [verCompletados, setVerCompletados] = useState(false);
  const [showDepositos, setShowDepositos] = useState(false);
  const [cierreId, setCierreId] = useState(null);

  const cargar = useCallback(async () => {
    if (!sucursalId) return;
    try { setData(await getDashboard(sucursalId)); setError(''); }
    catch (e) { setError(e.message || 'No se pudo cargar el dashboard'); }
  }, [sucursalId]);
  useEffect(() => { setData(null); cargar(); }, [cargar]);
  // Cuentas entre taquerías (salidas de productos): un indicador resumido; el detalle vive en su pantalla.
  const [entre, setEntre] = useState(null);
  useEffect(() => { if (sucursalId) getCuentasEntreSucursales(sucursalId).then((r) => setEntre(r.indicadores)).catch(() => setEntre(null)); }, [sucursalId]);

  const seguimientos = useMemo(() => (data?.seguimientos || []).filter((s) => verCompletados || s.estado !== 'Completado'), [data, verCompletados]);
  const completados = (data?.seguimientos || []).filter((s) => s.estado === 'Completado').length;

  const accion = (s) => {
    if (s.tipo === 'Depósito') return setShowDepositos(true);
    if (s.tipo === 'Proveedor') return navigate(`/Finanzas/CuentasPorPagar?ver=${String(s.id).replace(/^cxp-/, '')}`);
    if (s.tipo === 'Tarea') return navigate('/Finanzas/Agenda');
    if (s.tipo === 'Cierre') return s.cierreId ? setCierreId(s.cierreId) : navigate('/Finanzas/Cierres');
    if (s.tipo === 'Liquidación') return setCierreId(s.cierreId);
  };

  const d = data;
  return (
    <div className="dash-shell">
      <header className="dash-header">
        <div><h1>Dashboard</h1><p>¿Qué necesito atender?</p></div>
        <button className="fx-btn" onClick={() => navigate('/Finanzas/Agenda')}>Ver agenda</button>
      </header>
      {error && <p role="alert">{error}</p>}
      {!d && !error && <p className="fx-empty">Cargando…</p>}
      {d && <>
        {d.deposito.situacion && (
          <p role="alert" className="dash-alerta">
            {d.deposito.situacion.tipo === 'reserva_incompleta'
              ? `El efectivo en caja no alcanza para la reserva de la próxima apertura: faltan ${toMoney(d.deposito.situacion.monto)}.`
              : `Los depósitos superan el efectivo registrado por ${toMoney(d.deposito.situacion.monto)}. Revisa el detalle de depósitos.`}
          </p>
        )}
        <section className="dash-kpis" aria-label="Indicadores">
          <Kpi titulo="Dinero pendiente de depositar" valor={toMoney(d.deposito.pendiente)} tono={d.deposito.pendiente > 0 ? 'accion' : 'ok'}
            detalle={d.deposito.pendiente > 0 ? 'Pendiente de registrar' : 'Sin pendientes'} accion="Ver depósitos" onClick={() => setShowDepositos(true)} />
          <Kpi titulo="Cuentas por pagar" valor={toMoney(d.proveedores.totalPendiente)}
            detalle={`${plural(d.proveedores.conSaldo, 'proveedor', 'proveedores')} con saldo · ${d.proveedores.proximos} próximos · ${d.proveedores.vencidos} vencidos`}
            accion="Ver cuentas por pagar" onClick={() => navigate('/Finanzas/CuentasPorPagar')} />
          <Kpi titulo="Pagos vencidos" valor={`${toMoney(d.vencidos.monto)} vencidos`} tono={d.vencidos.monto > 0 ? 'alerta' : 'ok'}
            detalle={plural(d.vencidos.proveedores, 'proveedor', 'proveedores')} accion="Ver vencidas" onClick={() => navigate('/Finanzas/CuentasPorPagar')} />
          <Kpi titulo={`Próximos pagos · ${d.proximos.dias} días`} valor={toMoney(d.proximos.monto)} tono={d.proximos.pagos > 0 ? 'aviso' : 'ok'}
            detalle={plural(d.proximos.pagos, 'pago pendiente', 'pagos pendientes')} accion="Ver en agenda" onClick={() => navigate('/Finanzas/Agenda')} />
          {entre && <Kpi titulo="Cuentas entre taquerías" valor={toMoney(sucursalId === 'all' ? entre.saldoPendiente : entre.porCobrar - entre.porPagar)}
            tono={entre.saldoPendiente > 0 ? 'aviso' : 'ok'}
            detalle={sucursalId === 'all' ? `Saldo pendiente · ${plural(entre.cuentasAbiertas, 'cuenta abierta', 'cuentas abiertas')}`
              : `Por cobrar ${toMoney(entre.porCobrar)} · por pagar ${toMoney(entre.porPagar)}`}
            accion="Ver cuentas" onClick={() => navigate('/Finanzas/CuentasEntreTaquerias')} />}
        </section>

        {/* Liquidación: qué falta depositar/entregar/aplicar del efectivo de los cierres. */}
        <section className="dash-kpis" aria-label="Liquidación del efectivo">
          <Kpi titulo="Efectivo pendiente de liquidar" valor={toMoney(d.liquidacion.pendiente)} tono={d.liquidacion.pendiente > 0 ? 'aviso' : 'ok'}
            detalle="Efectivo de cierres sin destino registrado" accion="Ver cierres" onClick={() => navigate('/Finanzas/Cierres')} />
          <Kpi titulo="Cierres pendientes" valor={plural(d.liquidacion.cierresPendientes, 'cierre', 'cierres')} tono={d.liquidacion.cierresPendientes > 0 ? 'aviso' : 'ok'}
            detalle="Sin ningún movimiento de liquidación" />
          <Kpi titulo="Cierres parcialmente liquidados" valor={String(d.liquidacion.cierresParciales)} tono={d.liquidacion.cierresParciales > 0 ? 'aviso' : 'ok'}
            detalle="Con saldo por aplicar" />
        </section>

        <section className="fx-card">
          <div className="dash-seg-head">
            <h2>Tareas pendientes</h2>
            {completados > 0 && <label><input type="checkbox" checked={verCompletados} onChange={(e) => setVerCompletados(e.target.checked)} /> Mostrar completados ({completados})</label>}
          </div>
          {seguimientos.length ? (
            <table className="fx-table fx-table--stack">
              <thead><tr><th>Tarea</th><th>Sucursal</th><th>Cajero</th><th>Turno</th><th className="num">Monto</th><th>Fecha</th><th>Estado</th><th>Acción</th></tr></thead>
              <tbody>{seguimientos.map((s) => (
                <tr key={s.id} className={`dash-seg dash-seg--${s.estado === 'Próximo' ? 'proximo' : s.estado.toLowerCase()}`}>
                  <td data-label="Tarea"><b>{s.tarea || s.tipo}</b>{!s.tarea && <small className="dash-fecha">{s.descripcion}</small>}</td>
                  <td data-label="Sucursal">{s.sucursal || '—'}</td>
                  <td data-label="Cajero">{s.cajero || '—'}</td>
                  <td data-label="Turno">{s.turnoId ? nombreTurno(s.turnoId) : '—'}</td>
                  <td data-label="Monto" className="num">{s.monto != null ? toMoney(s.monto) : '—'}</td>
                  <td data-label="Vencimiento">{s.estado === 'Completado' ? fechaCorta(s.vencimiento) : relativo(s.diasParaVencer) || fechaCorta(s.vencimiento)}
                    {s.estado !== 'Completado' && s.diasParaVencer != null && Math.abs(s.diasParaVencer) > 1 && <small className="dash-fecha">{fechaCorta(s.vencimiento)}</small>}</td>
                  <td data-label="Estado">{s.avance ? <span className="fx-badge fx-badge--hoy">{s.avance}</span> : <EstadoBadge estado={s.estado} />}</td>
                  <td><button className="fx-btn" onClick={() => accion(s)}>{ACCION_TAREA[s.accion] || 'Ver'}</button></td>
                </tr>))}
              </tbody>
            </table>
          ) : <p className="fx-empty">No hay nada pendiente en los próximos {d.proximos.dias} días.</p>}
        </section>
      </>}

      {showDepositos && <DepositosModal sucursalId={sucursalId} isAdmin onClose={() => { setShowDepositos(false); cargar(); }} onSaved={cargar} />}
      {cierreId && <CierreDetalleModal cierreId={cierreId} onClose={() => { setCierreId(null); cargar(); }} onChanged={cargar} />}
    </div>
  );
}

